import {
    Box,
    Tab,
    Tabs,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useState } from "react";
import { GalleryCardComponent } from "../../components/GalleryCard";
import eventsData from "../../data/event/event.json";

// ─── Types ────────────────────────────────────────────────────────────────────

interface EventImage {
    id: number;
    image: string;
    height: string | null;
    width: string | null;
}

interface Event {
    id: string;
    name: string;
    images: EventImage[];
}

interface YearData {
    year: string;
    events: Event[];
}

// ─── Tab Panel Helper ─────────────────────────────────────────────────────────

interface TabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
    <Box
        role="tabpanel"
        hidden={value !== index}
        id={`tabpanel-${index}`}
        aria-labelledby={`tab-${index}`}
        sx={{ width: "100%" }}
    >
        {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </Box>
);

// ─── Image Grid ───────────────────────────────────────────────────────────────

const ImageGrid = ({ images }: { images: EventImage[] }) => (
    <Box
        sx={{
            display: "grid",
            gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(3, 1fr)",
            },
            justifyItems: "center",
            gap: 6,
            width: "100%",
        }}
    >
        {images.map((image) => (
            <GalleryCardComponent
                display="flex"
                key={image.id}
                height={image.height ?? undefined}
                width={image.width ?? undefined}
                id={image.id}
                path={image.image}
                alt="event image"
            />
        ))}
    </Box>
);

// ─── Main Events Component ────────────────────────────────────────────────────

const Events = () => {
    const data: YearData[] = eventsData as YearData[];

    const [selectedYear, setSelectedYear] = useState(0);
    const [selectedEvent, setSelectedEvent] = useState(0);

    const isSmallScreen = useMediaQuery("(max-width:650px)");

    const handleYearChange = (_: React.SyntheticEvent, newValue: number) => {
        setSelectedYear(newValue);
        setSelectedEvent(0);
    };

    const handleEventChange = (_: React.SyntheticEvent, newValue: number) => {
        setSelectedEvent(newValue);
    };

    return (
        <Box sx={{ width: "100%" }}>

            {/* ── Year Tabs ── */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
                <Tabs
                    value={selectedYear}
                    onChange={handleYearChange}
                    variant={isSmallScreen ? "scrollable" : "standard"}
                    scrollButtons={isSmallScreen ? "auto" : false}
                    centered={!isSmallScreen}
                    aria-label="Year tabs"
                    sx={{
                        "& .MuiTab-root": {
                            fontWeight: 600,
                            fontSize: "1rem",
                            textTransform: "none",
                            minWidth: 80,
                        },
                        "& .Mui-selected": {
                            color: "primary.main",
                        },
                    }}
                >
                    {data.map((yearData) => (
                        <Tab
                            key={yearData.year}
                            label={yearData.year}
                            id={`year-tab-${yearData.year}`}
                            aria-controls={`year-tabpanel-${yearData.year}`}
                        />
                    ))}
                </Tabs>
            </Box>

            {/* ── Per-Year Panel ── */}
            {data.map((yearData, yearIndex) => (
                <TabPanel key={yearData.year} value={selectedYear} index={yearIndex}>

                    {/* ── Event Tabs inside Year (only render when events exist) ── */}
                    <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                        {yearData.events.length > 0 && (
                            <Tabs
                                value={selectedEvent}
                                onChange={handleEventChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                aria-label="Event tabs"
                                sx={{
                                    "& .MuiTab-root": {
                                        fontWeight: 500,
                                        textTransform: "none",
                                        fontSize: "0.9rem",
                                    },
                                }}
                            >
                                {yearData.events.map((event) => (
                                    <Tab
                                        key={event.id}
                                        label={event.name}
                                        id={`event-tab-${event.id}`}
                                        aria-controls={`event-tabpanel-${event.id}`}
                                    />
                                ))}
                            </Tabs>
                        )}
                    </Box>

                    {/* ── Images for selected Event ── */}
                    {yearData.events.length === 0 ? (
                        <Typography
                            variant="body1"
                            sx={{ textAlign: "center", color: "text.secondary", py: 8 }}
                        >
                            No events found for this year.
                        </Typography>
                    ) : (
                        yearData.events.map((event, eventIndex) => (
                            <Box
                                key={event.id}
                                role="tabpanel"
                                hidden={selectedEvent !== eventIndex}
                                id={`event-tabpanel-${event.id}`}
                            >
                                {selectedEvent === eventIndex && (
                                    <>
                                        {event.images.length > 0 ? (
                                            <ImageGrid images={event.images} />
                                        ) : (
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    textAlign: "center",
                                                    color: "text.secondary",
                                                    py: 8,
                                                }}
                                            >
                                                No images available for this event.
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </Box>
                        ))
                    )}
                </TabPanel>
            ))}

            {/* ── Empty state ── */}
            {data.length === 0 && (
                <Typography
                    variant="body1"
                    sx={{ textAlign: "center", color: "text.secondary", py: 8 }}
                >
                    No events found.
                </Typography>
            )}
        </Box>
    );
};

export default Events;